import { Component, computed, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { AppAccountComponent } from './basics/account/account.component';
import { AppChatComponent } from './basics/chat/chat.component';
import { AppMenuComponent } from './basics/menu/menu.component';
import { AppSettingsComponent } from './basics/settings/settings.component';
import { AlphaHelper } from './helpers/alpha.helper';
import { SettingHelper } from './helpers/setting.helper';
import { StateHelper } from './helpers/state.helper';
import { WalletHelper } from './helpers/wallet.helper';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    CommonModule,
    RouterOutlet,
    AppAccountComponent,
    AppChatComponent,
    AppMenuComponent,
    AppSettingsComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {

  /**
   * Alpha helper
   */
  alpha_helper = inject(AlphaHelper);

  /**
   * Setting helper
   */
  setting_helper = inject(SettingHelper);

  /**
   * State helper
   */
  state_helper = inject(StateHelper);

  /**
   * Wallet helper
   */
  wallet_helper = inject(WalletHelper);

  /**
   * Page title
   */
  title = computed(() => this.state_helper.title());

  /**
   * Whether the screen size is of a desktop
   */
  is_desktop = signal(false);

  /**
   * Whether to show the left sidebar
   */
  show_left_sidebar = signal(false);

  /**
   * Whether to show the right sidebar
   */
  show_right_sidebar = signal(false);

  /**
   * Whether to show content overlay
   */
  show_overlay = signal(false);

  /**
   * Selected tab
   */
  selected_tab = signal('account');

  /**
   * Twitter url
   */
  twitter_url = signal(environment.twitter_url);

  /**
   * Discord url
   */
  discord_url = signal(environment.discord_url);

  /**
   * Construct component
   */
  constructor(
    private router: Router
  ) { }

  /**
   * Initialize component
   */
  ngOnInit() {
    this.updateSidebars();
    this.setting_helper.readStorage();
    this.wallet_helper.readStorage();
    this.wallet_helper.startTasks();
    this.alpha_helper.startTasks();
  }

  /**
   * Destroy component
   */
  ngOnDestroy() {
    this.wallet_helper.stopTasks();
    this.alpha_helper.stopTasks();
  }

  /**
   * Listen to window resize events
   */
  @HostListener('window:resize')
  onResize() {
    this.updateSidebars();
  }

  /**
   * Update the status of sidebars
   */
  updateSidebars() {
    // Detect screen size
    let desktop = false;
    if (window.innerWidth > 1280) {
      desktop = true;
    }

    this.is_desktop.set(desktop);
    this.show_left_sidebar.set(desktop);
    this.show_right_sidebar.set(desktop);
    this.show_overlay.set(false);
  }

  /**
   * Toggle left sidebar
   */
  toggleLeftSidebar() {
    this.show_left_sidebar.set(!this.show_left_sidebar());
    if (this.is_desktop()) {
      this.show_overlay.set(false);
    } else {
      this.show_right_sidebar.set(false);
      this.show_overlay.set(this.show_left_sidebar());
    }
  }

  /**
   * Toggle right sidebar
   */
  toggleRightSidebar() {
    this.show_right_sidebar.set(!this.show_right_sidebar());
    if (this.is_desktop()) {
      this.show_overlay.set(false);
    } else {
      this.show_left_sidebar.set(false);
      this.show_overlay.set(this.show_right_sidebar());
    }
  }

  /**
   * Close both sidebars
   */
  closeSidebars() {
    this.show_left_sidebar.set(false);
    this.show_right_sidebar.set(false);
    this.show_overlay.set(false);
  }

  /**
   * Select tab
   * 
   * @param tab 
   */
  selectTab(tab: string) {
    this.selected_tab.set(tab);
  }

  /**
   * Navigate to page
   * 
   * @param page
   */
  navigateToPage(page: string) {
    if (!this.is_desktop()) {
      this.closeSidebars();
    }
    this.router.navigateByUrl(page);
  }

  /**
   * Open link
   * 
   * @param link
   */
  openLink(link: string) {
    if (!this.is_desktop()) {
      this.closeSidebars();
    }
    window.open(link, "_blank");
  }
}
